<template>
  <div class="asc__nextgrid">
    <span v-if="this.requiredFields && this.requiredFields.length > 0 && this.showRequiredInfo">{{`${this.requiredFields.map(x => x.label).join()} ${$t('list.requiredFieldsMessage')}`}}</span>
    <b-table-simple hover bordered small responsive class="asc__nextgrid-table">
      <b-thead>
        <draggable tag="tr" :list="head" :disabled="disabledDraggable || mobileDragDisabled">
          <b-th
            v-for="header in head"
            :key="header.dataField+header.columnType"
            :style="header.minLength ? `min-width: ${header.minLength}px` : ''"
            :class="
              header.align == null ?
              'asc__nextgrid-table-header asc__nextgrid-table-header-' + header.columnType + ' text-left'
              :
              'asc__nextgrid-table-header asc__nextgrid-table-header-' + header.columnType + ' text-' + header.align"
          >
          <input v-if="header.columnType === 'selection'" type="checkbox" @click="selectAll()" v-model="allSelected">
            <span class="asc__nextgrid-table-header-title grid-wrap-text">{{header.label}}{{header.label && header.required ? '*' : ''}}
              <span class="asc__nextgrid-table-header-sort" v-if="header.columnType !== 'operations' && header.columnType !== 'selection'">
                <b-button
                  @click="sortable(header.dataField, sortableColumns[header.dataField] === 'ASC' ? 'DESC' : 'ASC', header.columnType)"
                  size="sm"
                  variant="light"
                  class="py-0"
                >
                  <i :class="sortableColumns[header.dataField] === 'ASC' ? 'fas fa-sort-up' : 'fas fa-sort-down'" />
                </b-button>
              </span>
            </span>
            <div class="asc__nextgrid-table-header-filter">
              <div v-if="header.modelControlUtil != null">
                <div v-if="header.modelControlUtil.InputType === 'AutoComplete'">
                  <autocomplete
                    @click="onClickAutoComplete(header)"
                    :search="onAutoCompleteSearch"
                    class="autocomplete-search"
                    @focus="disabledDraggable = true"
                    @blur="disabledDraggable = false"
                    :get-result-value="getResultValue"
                    @submit="handleSubmit(header.modelControlUtil.ModelProperty, $event)"
                    ref="AutoCompleteDropdown"
                    :disabled="disabledAutoComplete || header.disabled"
                  >
                    <template #result="{ result, props }">
                      <li v-bind="props">
                        <span>{{getResultValue(result)}}</span>
                      </li>
                    </template>
                  </autocomplete>
                </div>
                <div v-else>
                  <v-select
                    v-if="header.modelControlUtil.IsLookupTable"
                    label="Label"
                    :options="lookup[header.modelControlUtil.Code]"
                    @input="selectedValue(header.modelControlUtil.ModelProperty, $event, (header.columnType === 'DropDownCode' ? 'code' : 'lookup'))"
                    v-model="header.defaultValue"
                    @focus="disabledDraggable = true"
                    @blur="disabledDraggable = false"
                    :disabled="header.disabled"
                  >
                  </v-select>
                  <v-select
                    v-else
                    label="Description1"
                    :options="gridField[header.modelControlUtil.ModelProperty]"
                    @input="selectedValue(header.modelControlUtil.ModelProperty, $event, 'search')"
                    v-model="header.defaultValue"
                    @focus="disabledDraggable = true"
                    @blur="disabledDraggable = false"
                    :disabled="header.disabled"
                  >
                  </v-select>
                </div>
              </div>
              <v-select
                v-if="header.columnType === 'Boolean'"
                v-model="header.defaultValue"
                :options="searchBoolean"
                @input="filterBoolean(header)"
                label="title"
                @focus="disabledDraggable = true"
                @blur="disabledDraggable = false"
                :disabled="header.disabled"
              />
              <date-picker
                v-if="header.columnType === 'Date'"
                range
                type="date"
                :placeholder="getFormattedDate(header.defaultValue)"
                v-model="header.defaultValue"
                format="YYYY-MM-DD"
                value-type="format"
                @change="filterRangeDate(header.dataField, header.defaultValue, header.columnType)"
                @focus="disabledDraggable = true"
                @blur="disabledDraggable = false"
                :disabled="header.disabled"
              ></date-picker>

              <date-picker
                v-if="header.columnType === 'DateTime'"
                range
                type="date"
                :placeholder="getFormattedDate(header.defaultValue)"
                v-model="header.defaultValue"
                format="YYYY-MM-DD"
                value-type="format"
                @change="filterRangeDate(header.dataField, header.defaultValue, header.columnType)"
                @focus="disabledDraggable = true"
                @blur="disabledDraggable = false"
                :disabled="header.disabled"
              ></date-picker>

              <b-form-datepicker
                v-if="header.columnType === 'DateTime2'"
                v-model="header.defaultValue"
                placeholder=""
                @input="filterDate(header.dataField, header.defaultValue)"
                @focus="disabledDraggable = true"
                @blur="disabledDraggable = false"
                :disabled="header.disabled"
              />

              <b-form-input
                v-if="header.columnType === 'Time'"
                v-mask="'##:##:##'"
                placeholder="00:00:00"
                v-model="header.defaultValue"
                @keydown.enter="filterTime(header.dataField, header.defaultValue)"
                @focus="disabledDraggable = true"
                @blur="disabledDraggable = false"
                :disabled="header.disabled"
              />

              <b-form-input
                v-if="header.columnType === 'String'"
                v-model="header.defaultValue"
                @keydown.enter="searchOnTable(header.dataField, header.defaultValue)"
                @input="setSearchQ(header.dataField, $event)"
                @focus="disabledDraggable = true"
                @blur="disabledDraggable = false"
                :disabled="header.disabled"
              />

              <b-form-input
                v-if="header.columnType === 'Decimal'"
                type="number"
                v-model="header.defaultValue"
                @keydown.enter="filterDecimal(header.dataField, header.defaultValue)"
                @focus="disabledDraggable = true"
                @blur="disabledDraggable = false"
                :disabled="header.disabled"
              />

              <b-form-input
                v-if="header.columnType === 'Id'"
                v-model="header.defaultValue"
                @keydown.enter="searchOnTable(header.dataField, header.defaultValue)"
                @focus="disabledDraggable = true"
                @blur="disabledDraggable = false"
                :disabled="header.disabled"
              />
            </div>
          </b-th>
        </draggable>
      </b-thead>
      <b-tbody>
        <b-tr v-for="(item, i) in items" :key="i" :id="item.RecordId" @click.native="selectRow(item)" :class="(item.Selected || allSelected) ? 'row-selected' : '' || selectionMode === 'multi' ? 'multi-hover-class': ''" :variant="otherDispatchListLength ? updateRowAfterMultiPrint(item.RecordId, 'color') : ''">
          <b-tooltip v-if="isItemInDispatchList(item.RecordId)" :target=String(item.RecordId) position="bottom" >
            {{updateRowAfterMultiPrint(item.RecordId, 'message')}}
          </b-tooltip>
          <b-td v-for="h in head" :key="h.dataField+h.columnType">
            <span v-if="h.columnType === 'selection'" class="d-block w-100">
              <i v-if="selectionMode === 'multi'" class="fa fa-check-circle" :class="(item.Selected || allSelected) ? 'selected-color' : 'unselected-color'"></i>
            </span>
            <span v-if="h.columnType === 'operations'" class="d-block w-100">
              <b-dropdown v-if="tableOperations.RowActions.length >= 1" size="sm" variant="default" no-caret class="asc__nextgrid-dropdown-btn-p0">
                <template #button-content>
                  <i class="fas fa-th" />
                </template>
                <Actions :actions="filterRowActions(tableOperations.RowActions, item)" :row="item" @showModal="showModal" />
                <Workflow v-if="visibleWorkFlow(item)" :items="workFlowList" :RecordId="item.RecordId" />
              </b-dropdown>
            </span>
            <span v-else-if="h.columnType === 'LabelValue'" class="d-block w-100 grid-wrap-text" v-b-tooltip.hover :title="labelFormat(item[h.dataField], 'Label')">
              <template v-if="getLabelValueFormat(item, h)">
                {{ getLabelValueFormat(item, h) }}
              </template>
              <template v-else-if="h.label=='Durum'" >
                <span class="w-100 d-block text-center"> <i :class="iconClass(item)"></i>
                </span>
              </template>  
            </span>
            <span v-else-if="h.columnType === 'CodeValue'" class="d-block w-100 grid-wrap-text" v-b-tooltip.hover :title="labelFormat(item[h.dataField], 'Code')">
              {{ labelFormat(item[h.dataField], 'Code') }}
            </span>
            <span v-else-if="h.columnType === 'UpperValueValue'" class="d-block w-100 grid-wrap-text" v-b-tooltip.hover :title="abelFormat(item[h.dataField], 'UpperValue')">
              {{ labelFormat(item[h.dataField], 'UpperValue')}}
            </span>
            <span v-else-if="h.columnType === 'ValueValue'" class="d-block w-100 grid-wrap-text" v-b-tooltip.hover :title="labelFormat(item[h.dataField], 'Value')">
              {{ labelFormat(item[h.dataField], 'Value')}}
            </span>
            <span v-else-if="h.columnType === 'DecimalValueValue'" class="d-block w-100 grid-wrap-text" v-b-tooltip.hover :title="labelFormat(item[h.dataField], 'DecimalValue')">
              {{ labelFormat(item[h.dataField], 'DecimalValue')}}
            </span>
            <span v-else-if="h.columnType === 'OtherPropertiesValue'" class="d-block w-100 grid-wrap-text" v-b-tooltip.hover :title="labelFormat(item[h.dataField], 'OtherProperties')">
              {{ labelFormat(item[h.dataField], 'OtherProperties')}}
            </span>

            <span v-else-if="h.columnType === 'Boolean'" class="w-100 d-block text-center">
              <i :class="item[h.dataField] === 1 ? 'fas fa-check text-success' : 'fas fa-times text-danger'" />
            </span>
            <span v-else-if="h.columnType === 'Date'" class="d-block w-100 grid-wrap-text" v-b-tooltip.hover :title="dateFormat(item[h.dataField])">
              {{ dateFormat(item[h.dataField]) }}
            </span>
            <span v-else-if="h.columnType === 'DateTime'" class="d-block w-100 grid-wrap-text" v-b-tooltip.hover :title="dateTimeformat(item[h.dataField])">
              {{ dateTimeformat(item[h.dataField]) }}
            </span>
            <span v-else-if="h.columnType === 'DateTime2'" class="d-block w-100 grid-wrap-text" v-b-tooltip.hover :title="dateTimeformat(item[h.dataField])">
              {{ dateTimeformat(item[h.dataField]) }}
            </span>
            <span v-else-if="h.columnType === 'String'" class="d-block w-100 grid-wrap-text" v-b-tooltip.hover :title="item[h.dataField]">
              {{ item[h.dataField] }}
            </span>
            <span v-else-if="h.columnType === 'Id'" class="d-block w-100 grid-wrap-text" v-b-tooltip.hover :title="item[h.dataField]">
              <i>{{ item[h.dataField] }}</i>
            </span>
            <span v-else-if="h.columnType === 'DescriptionValue'" class="d-block w-100 grid-wrap-text" v-b-tooltip.hover :title="labelFormat(item['Label' + h.dataField], 'Description1')">
              {{ labelFormat(item['Label' + h.dataField], 'Description1') }}
            </span>
            <span v-else class="d-block w-100 grid-wrap-text" v-b-tooltip.hover :title="item[h.dataField]">
              {{ item[h.dataField] }}
            </span>
          </b-td>
        </b-tr>
      </b-tbody>
    </b-table-simple>
    <b-row class="asc__nextgrid-table-footer" v-if="items && items.length > 0">
      <b-col cols="6">
        <b-dropdown :text="perPage + ' / ' + totalRowCount" size="sm">
          <b-dropdown-item v-for="p in perPageOpt" :key="'perpage' + p" @click="setPerPage(p)" active-class="dropdown-active">{{p}}</b-dropdown-item>
        </b-dropdown>
      </b-col>
      <b-col cols="6">
        <b-pagination-nav pills :link-gen="linkGen" :number-of-pages="totalPageCount" use-router variant="dark" class="float-right asc__nextgrid-paginationlinks" />
      </b-col>
    </b-row>
    <b-modal id="approve-reject-modal" ref="RejectModal" hide-footer hide-header>
      <PotentialCustomerRejectModal :modalAction="modalAction" :modalItem="modalItem" />
    </b-modal>
    <b-modal id="approve-modal" ref="ApproveModal" hide-footer hide-header>
      <PotentialCustomerApproveModal v-if="showPotentialCustomerApproveModal" :modalAction="modalAction" :modalItem="modalItem" />
    </b-modal>
    <b-modal id="location-modal" ref="LocationModal" hide-footer hide-header>
      <NextLocation v-if='showLocationModal' :Location="modalItem"/>
    </b-modal>
    <ConfirmModal v-if="showConfirmModal" :modalAction="modalAction" :modalItem="modalItem" :after-action="afterAction" />
    <CustomConvertModal v-if="showCustomConvertModal" :modalAction="modalAction" :modalItem="modalItem" />
    <OrderConvertModal v-if="showConvertModal" :openModal="showConvertModal" :modalAction="modalAction" :modalItem="modalItem" />
    <SalesWaybillConvertModal v-if="showWaybillConvertModal" :modalAction="modalAction" :modalItem="modalItem" />
    <RmaConvertModal v-if="showRmaConvertModal" :modalAction="modalAction" :modalItem="modalItem" />
    <RmaInvoiceModal v-if="showRmaInvoiceModal" :modalAction="modalAction" :modalItem="modalItem" />
    <InvoiceConvertModal v-if="showInvoiceConvertModal" :modalAction="modalAction" :modalItem="modalItem" />
    <KmModal v-if="showKmModal" :modalAction="modalAction" :modalItem="modalItem" />
    <CreateSalesWaybill v-if="showCreateSalesWaybillModal" :modalAction="modalAction" :modalItem="modalItem" />
    <MultipleLoadingPlanModal v-if="showMultipleLoadingPlanModal" :modalAction="modalAction" :modalItem="modalItem" />
    <UpdateCreditBudgetModal v-if="showUpdateCreditBudgetModal" :modalAction="modalAction" :modalItem="modalItem" />
    <BudgetMasterApproveModal v-if="showBudgetMasterApproveModal" :modalAction="modalAction" :modalItem="modalItem" />
    <AssignEmployeeModal v-if="showAssignEmployeeModal" :modalAction="modalAction" :modalItem="modalItem" />
    <CommonInfoModal v-if="showCommonInfoModal" :modalAction="modalAction" :modalItem="modalItem" />
    <EFULogModal v-if="showEFULogModal" :modalAction="modalAction" :modalItem="modalItem" />
    <CreditBudgetBulkApproveModal v-if="showCreditBulkBudgetModal" :modalAction="modalAction" :modalItem="modalItem" size="xl"/>
  </div>
</template>
<script>
import { mapState, mapActions } from 'vuex'
import mixin from '../mixins/index'
import Workflow from './Workflow'
import ConfirmModal from './Actions/ConfirmModal'
import CustomConvertModal from './Actions/CustomConvertModal'
import OrderConvertModal from './Actions/OrderConvertModal'
import SalesWaybillConvertModal from './Actions/SalesWaybillConvertModal'
import RmaConvertModal from './Actions/RmaConvertModal'
import RmaInvoiceModal from './Actions/RmaInvoiceModal'
import InvoiceConvertModal from './Actions/InvoiceConvertModal'
import KmModal from './Actions/KmModal'
import CreateSalesWaybill from './Actions/CreateSalesWaybill'
import MultipleLoadingPlanModal from './Actions/MultipleLoadingPlanModal'
let searchQ = {}
export default {
  components: {
    Workflow,
    ConfirmModal,
    CustomConvertModal,
    OrderConvertModal,
    SalesWaybillConvertModal,
    RmaConvertModal,
    RmaInvoiceModal,
    KmModal,
    InvoiceConvertModal,
    CreateSalesWaybill,
    MultipleLoadingPlanModal
  },
  props: {
    apiurl: {
      type: String,
      default: '',
      description: 'Listeleme için kullanacak api url bilgisi'
    },
    apiparams: {
      type: String,
      default: '',
      description: 'Api parametre bilgisi'
    },
    andConditionalModel: {
      type: Object,
      default: function () {
        return {}
      },
      description: 'Listeleme için andCondition bilgisi'
    },
    workFlowModel: {
      type: Object,
      default: function () {
        return {}
      },
      description: 'Index ekranları için workFlow bilgisi'
    },
    selectionMode: {
      type: String,
      validator: (prop) => [
        'none',
        'single',
        'multi'
      ].includes(prop),
      default: 'none',
      description: 'Grid satırlarının seçilebilirlik ayarları'
    },
    OrderByColumns: {
      type: Array,
      default: () => [],
      description: 'Listeleme için orderByColumns bilgisi'
    },
    actionCondition: {
      type: Object,
      default: null,
      description: 'Listeleme için andCondition bilgisi'
    },
    afterActions: {
      type: Array,
      default: () => [],
      description: 'Ortak actionların ardından çağrılacak action listesi'
    }
  },
  mixins: [mixin],
  data () {
    return {
      grid: [],
      modalActionUrl: '',
      modalRecordId: '',
      modalRecord: '',
      modalQuery: '',
      modalQueryMessage: '',
      head: [],
      items: null,
      totalRowCount: null,
      totalPageCount: null,
      perPageOpt: [20, 50, 100],
      perPage: 20,
      currentPage: 1,
      searchText: null,
      tablefield: null,
      searched: null,
      sort: 'ASC',
      sortField: null,
      searchBoolean: [
        { value: 1, title: 'Aktif' },
        { value: 0, title: 'Pasif' }
      ],
      rangeDate: [],
      selectedHeader: null,
      autoCompleteAndConditions: null,
      workFlowList: [],
      selectedItems: [],
      requiredFields: [],
      showRequiredInfo: true,
      modalAction: null,
      modalItem: null,
      isGridFieldsReady: false,
      isLookupReady: false,
      showConvertModal: false,
      showWaybillConvertModal: false,
      showRmaConvertModal: false,
      showRmaInvoiceModal: false,
      showInvoiceConvertModal: false,
      showPotentialCustomerApproveModal: false,
      showCustomConvertModal: false,
      showConfirmModal: false,
      showKmModal: false,
      showLocationModal: false,
      showCreateSalesWaybillModal: false,
      autoSearchInput: {},
      showMultipleLoadingPlanModal: false,
      AndConditionalModel: {},
      disabledDraggable: false,
      firstHead: null,
      firstSearchItem: null,
      showUpdateCreditBudgetModal: false,
      disabledAutoComplete: false,
      showBudgetMasterApproveModal: false,
      showAssignEmployeeModal: false,
      showCommonInfoModal: false,
      showEFULogModal: false,
      showCreditBulkBudgetModal: false,
      sortableColumns: {},
      mobileDragDisabled: false,
      afterAction: null,
      allSelected: false,
      recordIds: []
    }
  },
  mounted () {
    this.mobileDragDisabled = this.isMobile()
    searchQ = {}
    this.$store.commit('setIsMultipleGrid', this.selectionMode === 'multi')
    this.$forceUpdate()
    this.$store.commit('setLastGridItem', null)
    this.$store.commit('setLastGridModel', {})
    let sortOpt = {}
    // ön tanımlı olarak 20 kayıt gelir. eğer farklı bir değer seçilmişse onu belirtir.
    if (this.$route.query.count) {
      this.perPage = this.$route.query.count
    } else {
      this.perPage = 20
    }
    // ön tanımlı olarak 1. sayfa gelir. eğer farklı bir sayfa istenmişse onu belirtir.
    if (this.$route.query.page) {
      this.currentPage = parseInt(this.$route.query.page)
    } else {
      this.currentPage = 1
      this.$route.query.page = 1
    }
    // ön tanımlı olarak sıralama gönderilmez. eğer varsa query alınacak.
    if (this.$route.query.sort) {
      this.sort = this.$route.query.sort
      this.sortField = this.$route.query.field
      sortOpt = {
        table: this.sortField,
        sort: this.sort
      }
    } else {
      sortOpt = null
    }
    this.AndConditionalModel = this.andConditionalModel
    this.getData(this.$route.name, this.currentPage, this.perPage, sortOpt, true)
    this.getWorkflowData()
    this.$store.commit('setSelectedTableRows', [])
    this.$store.dispatch('acSetOtherDispatchListToDefault', [{'id': null, 'message': ''}]) // Renklendirilmiş satırların eski haline dönmesini sağlar
  },
  computed: {
    ...mapState(['tableData', 'tableOperations', 'tableRows', 'tableRowsAll', 'selectedTableRows', 'nextgrid', 'gridField', 'lookup', 'filtersCleared', 'lastGridItem', 'reloadGrid', 'otherDispatchMultiPrintList']),
    ...mapActions(['acSetOtherDispatchListToDefault']),
    otherDispatchListLength () {
      if (this.otherDispatchMultiPrintList.length > 1) {
        return true
      } else {
        return false
      }
    }
  },
  methods: {
    selectAll (item) {
      this.recordIds = []
      this.allSelected = !this.allSelected
      if (this.allSelected) {
        for (item in this.items) {
          let ids = this.items[item]
          this.recordIds.push(ids)
        }
        console.log(this.recordIds)
        console.log(this.selectedItems)
        let allItems = this.recordIds.filter(r => r.RecordId === item.RecordId)
        console.log(allItems)
        if (allItems && allItems.length > 0) {
          this.recordIds.splice(this.recordIds.indexOf(allItems))
        }
        console.log(this.recordIds)
      }
      this.$store.commit('setSelectedTableRows', this.recordIds)
    },
    getWorkflowData () {
      if (this.workFlowModel && this.workFlowModel.ControllerName && this.workFlowModel.ClassName && this.workFlowModel.PageName) {
        this.$api.post(this.workFlowModel, 'Workflow', 'Workflow/GetWorkflowList').then((res) => {
          this.workFlowList = res.ListModel.BaseModels
        })
      }
    },
    showModal (action, row) {
      this.modalAction = action
      this.modalItem = row
      this.showConvertModal = false
      this.showCustomConvertModal = false
      this.showWaybillConvertModal = false
      this.showRmaConvertModal = false
      this.showRmaInvoiceModal = false
      this.showPotentialCustomerApproveModal = false
      this.showPotentialCustomerRejectModal = false
      this.showConfirmModal = false
      this.showKmModal = false
      this.showLocationModal = false
      this.showCreateSalesWaybillModal = false
      this.showMultipleLoadingPlanModal = false
      this.showUpdateCreditBudgetModal = false
      this.showBudgetMasterApproveModal = false
      this.showAssignEmployeeModal = false
      this.showCommonInfoModal = false
      this.showEFULogModal = false
      this.showCreditBulkBudgetModal = false

      if (!this.validateAction(action.Action)) {
        return
      }
      if (action.Action === 'RejectPotentialCustomer' || action.Action === 'ApprovePotentialCustomer') {
        if (row.ApproveStateId !== 51) {
          this.$toasted.show(this.$t('index.errorApproveStateModal'), {
            type: 'error',
            keepOnHover: true,
            duration: '3000'
          })
          return
        }
      }
      if (action.Action === 'ApprovePotentialCustomer') {
        this.showPotentialCustomerApproveModal = true
        this.$nextTick(() => {
          this.$root.$emit('bv::show::modal', 'approve-modal')
        })
      } else if (action.Action === 'RejectPotentialCustomer') {
        this.$root.$emit('bv::show::modal', 'approve-reject-modal')
      } else if (action.Action === 'CustomConvert') {
        this.showCustomConvertModal = true
        this.$nextTick(() => {
          this.$root.$emit('bv::show::modal', 'customConvertModal')
        })
      } else if (action.Action === 'OrderConvert') {
        if (row && typeof row.StatusId !== 'undefined' && row.StatusId !== 2 && this.$route.name !== 'PurchaseOrder') {
          this.$toasted.show(this.$t('index.errorSevk'), {
            type: 'error',
            keepOnHover: true,
            duration: '3000'
          })
          return
        }
        this.showConvertModal = true
        this.$nextTick(() => {
          this.$bvModal.show('orderConvertModal')
        })
      } else if (action.Action === 'WaybillConvert') {
        this.showWaybillConvertModal = true
        this.$nextTick(() => {
          this.$root.$emit('bv::show::modal', 'salesWaybillConvertModal')
        })
      } else if (action.Action === 'KmModal') {
        this.showKmModal = true
        this.$nextTick(() => {
          this.$root.$emit('bv::show::modal', 'kmModal')
        })
      } else if (action.Action === 'ShowOnMap') {
        this.showLocationModal = true
        this.$nextTick(() => {
          this.$root.$emit('bv::show::modal', 'location-modal')
        })
      } else if (action.Action === 'OrderRmaConvert') {
        if (row && typeof row.RmaStatusId !== 'undefined' && row.RmaStatusId !== 3) {
          this.$toasted.show(this.$t('index.errorConfirm'), {
            type: 'error',
            keepOnHover: true,
            duration: '3000'
          })
          return
        }
        this.showRmaConvertModal = true
        this.$nextTick(() => {
          this.$root.$emit('bv::show::modal', 'rmaConvertModal')
        })
      } else if (action.Action === 'CreateDespatchWaybill') {
        if (this.modalItem && this.modalItem.EDocumentStatus && (this.modalItem.EDocumentStatus.Code === 'SendToGib' || this.modalItem.EDocumentStatus.Code === 'SendToReceiver')) {
          this.showCreateSalesWaybillModal = true
          this.$nextTick(() => {
            this.$root.$emit('bv::show::modal', 'createSalesWaybillModal')
          })
        } else {
          this.$toasted.show(this.$t('index.Convert.createSalesWaybillStatusError'), { type: 'error', keepOnHover: true, duration: '4000' })
        }
      } else if (action.Action === 'LoadingPlanMultiple') {
        this.showMultipleLoadingPlanModal = true
        this.$nextTick(() => {
          this.$root.$emit('bv::show::modal', 'multipleLoadingPlanModal')
        })
      } else if (action.Action === 'BudgetUpdate') {
        this.showUpdateCreditBudgetModal = true
        this.$nextTick(() => {
          this.$root.$emit('bv::show::modal', 'update-credit-budget-modal')
        })
      } else if (action.Action && action.Action.trim() === 'BudgetMasterApprove') {
        this.showBudgetMasterApproveModal = true
        this.$nextTick(() => {
          this.$root.$emit('bv::show::modal', 'budget-master-approve-modal')
        })
      } else if (action.Action === 'AssignEmployee') {
        this.showAssignEmployeeModal = true
        this.$nextTick(() => {
          this.$root.$emit('bv::show::modal', 'assign-employee-modal')
        })
      } else if (action.Action === 'BranchCustomConvert') {
        this.showCustomConvertModal = true
        this.$nextTick(() => {
          this.$root.$emit('bv::show::modal', 'customConvertModal')
        })
      } else if (action.Action === 'Info') {
        this.showCommonInfoModal = true
        this.$nextTick(() => {
          this.$bvModal.show('common-info-modal')
        })
      } else if (action.Action === 'EfuLog') {
        this.showEFULogModal = true
        this.$nextTick(() => {
          this.$bvModal.show('efu-log-modal')
        })
      } else if (action.Action === 'ContractCustomerApprove') {
        this.showCreditBulkBudgetModal = true
        this.$nextTick(() => {
          this.$root.$emit('bv::show::modal', 'credit-budget-bulk-approve-modal')
        })
      } else {
        let findedAction = this.afterActions.find(a => a.action === action.Action)
        this.afterAction = findedAction ? findedAction.actionMethod : null

        this.showConfirmModal = true
        this.$nextTick(() => {
          this.$root.$emit('bv::show::modal', 'confirmModal')
        })
      }
      // if (action.Action === 'RmaInvoice') {
      //   this.showRmaInvoiceModal = true
      //   this.$nextTick(() => {
      //     this.$root.$emit('bv::show::modal', 'rmaInvoiceModal')
      //   })
      //   return
      // }
      // if (action.Action === 'PurchaseInvoiceConvert') {
      //   this.showInvoiceConvertModal = true
      //   this.$nextTick(() => {
      //     this.$root.$emit('bv::show::modal', 'invoiceConvertModal')
      //   })
      //   return
      // }
    },
    dateTimeformat (e) {
      let calendar, date
      if (e != null) {
        calendar = e.split('T')
        date = calendar[0].split('-')
        return date[2] + '/' + date[1] + '/' + date[0] + ' ' + calendar[1]
      }
    },
    dateFormat (e) {
      return e
    },
    labelFormat (e, type) {
      if (e == null) {
        return ''
      } else {
        return e[type]
      }
    },
    iconClass(value) {
      if (value.StatusId === 1) {
        return 'fas fa-check text-success'; // Icon for value 1
      } else if (value.StatusId === 2) {
        return 'fas fa-times text-danger'; // Icon for value 2
      } else {
        return ''; // Default icon for other values
      }
    },
    getLabelValueFormat (item, header) {
      if (!item || !header) { return '' }

      const obj = item[header.dataField]
      if (header.modelControlUtil && header.modelControlUtil.InputType === 'AutoComplete' && header.modelControlUtil.ModelProperty === 'RepresentativeIds' && obj && obj.Code) {
        return `${obj.Label}`
      }
      if (header.modelControlUtil && header.modelControlUtil.InputType === 'AutoComplete' && header.modelControlUtil.ModelProperty !== 'RepresentativeIds' && obj && obj.Code) {
        return `${obj.Code} - ${obj.Label}`
      }

      return this.labelFormat(obj, 'Label')
    },
    linkGen (pageNum) {
      let paginationQ = ''
      const rt = this.$route.query
      if (rt.search) {
        paginationQ += `&search=${rt.search}&where=${rt.where}`
      }
      if (rt.sort) {
        paginationQ += `&field=${rt.field}&sort=${rt.sort}`
      }
      if (rt.count) {
        paginationQ += `&count=${rt.count}`
      }
      return `?page=${pageNum}${paginationQ}`
    },
    setPerPage (p) {
      this.perPage = p
      if (this.$route.query.sort) {
        this.$router.push({name: this.$route.name, query: {'page': 1, 'count': p, 'field': this.$route.query.field, 'sort': this.$route.query.sort}})
      } else {
        this.$router.push({name: this.$route.name, query: {'page': 1, 'count': p}})
      }
    },
    sortable (field, sort, columnType) {
      let convertedField = ''
      if (columnType === 'LabelValue') {
        convertedField = `${field}.Description1`
      } else if (columnType === 'CodeValue') {
        convertedField = `${field}.Code`
      } else {
        convertedField = field
      }
      this.sort = sort
      this.sortableColumns = {}
      this.sortableColumns[field] = sort
      this.sortField = convertedField
      const rt = this.$route.query
      let routeQ = {}
      routeQ['page'] = 1
      if (rt.search) {
        routeQ['search'] = rt.search
        routeQ['where'] = rt.where
      }
      if (rt.count) {
        routeQ['count'] = rt.count
      }
      routeQ['field'] = this.sortField
      routeQ['sort'] = this.sort
      this.$router.push({name: this.$route.name, query: routeQ})
    },
    filterAutocomplete (e) {
      this.searchOnTable(e, this.searchText.id)
    },
    filterBoolean (e) {
      let search = e.defaultValue ? e.defaultValue.value : ''
      this.searchOnTable(e.dataField, search)
    },
    filterDate (e, date) {
      let model = {
        Value: this.dateConvertToISo(date)
      }
      this.AndConditionalModel[e] = model
      this.searchOnTable()
    },
    filterRangeDate (e, date, type) {
      if (date[0] && date[1]) {
        let beginValue = this.dateConvertToISo(date[0])
        let endValue = this.dateConvertToISo(date[1])
        let endDate = new Date(date[1])

        switch (type) {
          case 'DateTime':
            if (date[0] === date[1]) {
              endValue = new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59)).toISOString()
            }
            break
          case 'Date':
            beginValue = beginValue.substr(0, 10)
            if (date[0] === date[1]) {
              endValue = new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), (endDate.getDate() + 1), 0, 0, 0)).toISOString().substr(0, 10)
            } else {
              endValue = endValue.substr(0, 10)
            }
            break
        }

        let model = {
          BeginValue: beginValue,
          EndValue: endValue
        }
        this.AndConditionalModel[e] = model
        this.currentPage = 1
      } else {
        this.AndConditionalModel[e] = null
        this.currentPage = 1
      }
      this.searchOnTable()
    },
    filterTime (e, time) {
      this.searchOnTable(e, time)
    },
    filterLabel (e, i) {
      this.searchOnTable(`${e}Ids`, [i.RecordId])
    },
    filterDecimal (e, i) {
      this.searchOnTable(e, parseFloat(i))
    },
    selectedValue (label, model, type) {
      if (model) {
        this.currentPage = 1
        if (!this.AndConditionalModel) {
          this.AndConditionalModel = {}
        }
        if (type === 'lookup') {
          this.AndConditionalModel[label] = [model.DecimalValue]
        } else if (type === 'code') {
          this.AndConditionalModel[label] = model.Code
        } else {
          this.AndConditionalModel[label] = [model.RecordId]
        }
        this.searchOnTable()
      } else {
        delete this.AndConditionalModel[label]
        this.searchOnTable()
      }
    },
    onAutoCompleteSearch (input) {
      if (input === '') { return [] }
      if (this.selectedHeader && this.selectedHeader.ModelProperty) {
        if (input === '' || this.autoSearchInput[this.selectedHeader.ModelProperty] === input) {
          this.autoSearchInput[this.selectedHeader.ModelProperty] = input
          return []
        }
        this.autoSearchInput[this.selectedHeader.ModelProperty] = input
      }
      if (input.length < 3) { return [] }
      let pagerecordCount = 10
      const orConditionModels = input === '%%%' ? [] : [
        {
          Description1: input.replaceAll('%', ''),
          Code: input.replaceAll('%', '')
        }
      ]
      let andConditions = this.getAndConditionModel(this.autoCompleteAndConditions)
      return this.$store.dispatch('getAutoGridFields', {...this.query, serviceUrl: this.selectedHeader.ServiceUrl, val: this.selectedHeader.ModelProperty, orConditionModels: orConditionModels, pagerecordCount: pagerecordCount, model: andConditions}).then((res) => {
        if (res && res.length === 0) {
          this.$bvToast.toast(this.$t('general.noResult'), {
            title: this.$t('general.warningTitle'),
            variant: 'warning',
            toaster: 'b-toaster-top-right',
            noCloseButton: false
          })
        }
        return res
      })
    },
    getResultValue (result) {
      if (this.selectedHeader.columnType === 'CodeValue') {
        return result.Code
      } else if (this.selectedHeader.dataField === 'Customer') {
        const status = result.StatusId === 2 ? this.$t('insert.passive') : this.$t('insert.active')
        return result.Code
          ? `${result.Code} - ${result.Description1} - ${status}`
          : `${result.Description1} - ${status}`
      } else {
        return result.Code ? `${result.Code} - ${result.Description1}` : result.Description1
      }
    },
    handleSubmit (label, model) {
      if (!model) {
        this.$bvToast.toast(JSON.stringify(this.$t('insert.autoCompleteClickError')), {
          title: this.$t('general.errorTitle'),
          variant: 'danger',
          toaster: 'b-toaster-top-right',
          noCloseButton: false
        })
        return
      }
      if (model) {
        this.currentPage = 1
        if (!this.AndConditionalModel) {
          this.AndConditionalModel = {}
        }
        this.AndConditionalModel[label] = [model.RecordId]
        this.searchOnTable()
      } else {
        delete this.AndConditionalModel[label]
        this.searchOnTable()
      }
    },
    searchOnTable (tableField, search) {
      if (search || search === 0) {
        this.currentPage = 1
      }
      if (!this.firstSearchItem) {
        this.firstSearchItem = {
          andConditionalModel: { ...this.AndConditionalModel },
          searchQ: { ...searchQ }
        }
      }
      let validCount = 0
      this.requiredFields.forEach(r => {
        if (searchQ[r.field] || this.AndConditionalModel[r.field] || this.AndConditionalModel[`${r.field}Ids`]) {
          validCount++
        }
      })
      if (validCount < this.requiredFields.length) {
        return
      }
      this.showRequiredInfo = false
      if ((search && search !== '') || search === 0) {
        searchQ[tableField] = search
      } else {
        delete searchQ[tableField]
      }
      this.disabledAutoComplete = true
      let sortOpt = {}
      if (this.$route.query.sort) {
        this.sort = this.$route.query.sort
        this.sortField = this.$route.query.field
        sortOpt = {
          table: this.sortField,
          sort: this.sort
        }
      } else {
        sortOpt = null
      }
      this.$store.dispatch('getTableData', {
        ...this.query,
        apiUrl: this.apiurl,
        api: this.$route.name,
        page: this.currentPage,
        count: this.perPage,
        search: searchQ,
        andConditionalModel: this.AndConditionalModel,
        OrderByColumns: this.OrderByColumns,
        sort: sortOpt
      }).then(() => {
        this.disabledAutoComplete = false
      }).catch(() => {
        this.disabledAutoComplete = false
      })
    },
    getData (e, p, c, s, requiredFieldsError) {
      this.$store.dispatch('getTableOperations', {
        ...this.query,
        apiUrl: this.apiurl,
        def: this.apiparams,
        api: e,
        page: parseInt(p),
        count: parseInt(c),
        sort: s,
        code: this.$route.query.code,
        andConditionalModel: this.AndConditionalModel,
        requiredFieldsError: requiredFieldsError
      })
    },
    closeApproveModal () {
      this.$root.$emit('bv::hide::modal', 'approve-modal')
      this.$root.$emit('bv::hide::modal', 'approve-reject-modal')
    },
    onClickAutoComplete (header) {
      this.selectedHeader = header.modelControlUtil
      const headerProps = {
        dataField: header.dataField,
        columnType: header.columnType
      }
      if (!this.selectedHeader) {
        this.selectedHeader = headerProps
      } else {
        this.selectedHeader = {...this.selectedHeader, ...headerProps}
      }
      this.autoCompleteAndConditions = header.AndConditions
    },
    isSelectable () {
      return this.selectionMode === 'single' || this.selectionMode === 'multi'
    },
    selectRow (item) {
      if (!this.visibleWorkFlow(item)) {
        return
      }
      if (this.selectionMode === 'multi') {
        item.Selected = !item.Selected
        let includedItems = this.selectedItems.filter(s => s.RecordId === item.RecordId)
        if (includedItems && includedItems.length > 0) {
          this.selectedItems.splice(this.selectedItems.indexOf(includedItems[0]), 1)
        } else {
          this.selectedItems.push(item)
        }
        this.$forceUpdate()
      } else if (this.selectionMode === 'single') {
        this.items.map(i => {
          i.Selected = false
          return i
        })
        item.Selected = true
        this.selectedItems = [item]
        this.$forceUpdate()
      }
      this.$store.commit('setSelectedTableRows', this.selectedItems)
    },
    setDefaultValues (visibleRows) {
      if (!this.AndConditionalModel) {
        this.AndConditionalModel = {}
      }
      var me = this
      for (let i = 0; i < visibleRows.length; i++) {
        let row = this.tableRowsAll.find(c => c.dataField === visibleRows[i].dataField)
        if (!row.defaultValue) {
          continue
        }
        if (row.modelControlUtil !== null) {
          if (typeof row.defaultValue === 'object') {
            continue
          }
          let value = parseInt(row.defaultValue)
          if (row.modelControlUtil.InputType === 'AutoComplete') {
            me.AndConditionalModel[row.modelControlUtil.ModelProperty] = [value]
          } else {
            if (row.modelControlUtil.InputType === 'DropDown' && me.gridField && me.gridField[row.modelControlUtil.ModelProperty]) {
              row.defaultValue = me.gridField[row.modelControlUtil.ModelProperty].find(l => l.RecordId === value)
              this.AndConditionalModel[row.modelControlUtil.ModelProperty] = [value]
            }
            if (row.modelControlUtil.IsLookupTable && me.lookup && me.lookup[row.modelControlUtil.Code]) {
              row.defaultValue = me.lookup[row.modelControlUtil.Code].find(l => l.DecimalValue === value)
              if (row.columnType === 'DropDownCode') {
                this.AndConditionalModel[row.modelControlUtil.ModelProperty] = row.defaultValue ? row.defaultValue.Code : null
              } else {
                this.AndConditionalModel[row.modelControlUtil.ModelProperty] = [value]
              }
            }
          }
        } else {
          switch (row.columnType) {
            case 'Boolean':
              if (row.defaultValue && typeof row.defaultValue !== 'object') {
                let value = parseInt(row.defaultValue)
                row.defaultValue = me.searchBoolean.find(b => b.value === value)
                searchQ[row.dataField] = value
              }
              break
            case 'Date':
            case 'DateTime':
              if (row.defaultValue && typeof row.defaultValue !== 'object') {
                let model = {}
                let today = new Date()
                let todayDate = new Date(today)
                let firstDayOfMonth = new Date(todayDate.setDate(1))
                let firstDate = new Date(firstDayOfMonth)
                let nextMonth = new Date(firstDate.setMonth(firstDate.getMonth() + 1))
                let lastDayOfMonth = new Date(nextMonth.setDate(0))
                switch (row.defaultValue) {
                  case 'first':
                    model.BeginValue = me.dateConvertToISo(firstDayOfMonth)
                    model.EndValue = me.dateConvertToISo(lastDayOfMonth)
                    break
                  case 'last':
                    model.BeginValue = me.dateConvertToISo(lastDayOfMonth)
                    model.EndValue = me.dateConvertToISo(lastDayOfMonth)
                    break
                  case 'today':
                    model.BeginValue = me.dateConvertToISo(today)
                    model.EndValue = me.dateConvertToISo(today)
                    break
                  case 'firsttoday':
                    model.BeginValue = me.dateConvertToISo(firstDayOfMonth)
                    model.EndValue = me.dateConvertToISo(today)
                    break
                  case 'todaylast':
                    model.BeginValue = me.dateConvertToISo(today)
                    model.EndValue = me.dateConvertToISo(lastDayOfMonth)
                    break
                }
                if (row.columnType === 'Date') {
                  model.BeginValue = model.BeginValue.substr(0, 10)
                  model.EndValue = model.EndValue.substr(0, 10)
                }
                row.defaultValue = [model.BeginValue, model.EndValue]
                me.AndConditionalModel[row.dataField] = model
              }
              break
            case 'String':
            case 'Id':
            case 'Decimal':
              searchQ[row.dataField] = row.defaultValue
              break
          }
        }
        visibleRows[i] = row
      }
      return visibleRows
    },
    setRows () {
      let lookups = ''
      let hasAnyDropdown = false
      this.tableRows.forEach(c => {
        let row = this.tableRowsAll.find(t => t.dataField === c.dataField)
        let control = row.modelControlUtil
        if (control != null && control.InputType === 'DropDown') {
          if (control.IsLookupTable) {
            lookups += control.Code + ','
          } else {
            hasAnyDropdown = true
            let andConditions = this.getAndConditionModel(row.AndConditions)
            this.$store.dispatch('getGridFields', {...this.query, serviceUrl: control.ServiceUrl, val: control.ModelProperty, model: andConditions}).then(() => {
              this.isGridFieldsReady = true
            })
          }
        }
      })
      if (!hasAnyDropdown) {
        this.isGridFieldsReady = true
      }
      if (lookups.length > 0) {
        lookups = lookups.slice(0, -1)
        this.$store.dispatch('getAllLookups', {...this.query, type: lookups}).then(() => {
          this.isLookupReady = true
        })
      } else {
        this.isLookupReady = true
      }
    },
    setRowsAfter () {
      if (!this.isGridFieldsReady || !this.isLookupReady) {
        return
      }
      this.head = []
      let visibleRows = this.tableRows.filter(item => item.visible === true)
      visibleRows = this.setDefaultValues(visibleRows)
      this.requiredFields = visibleRows.filter(v => v.required === true).map(x => {
        let requiredField = {
          field: x.dataField,
          label: x.label
        }
        return requiredField
      })
      const selection = { columnType: 'selection', dataField: null, label: null, minLength: 30, allowHide: false, allowSort: false }
      const opt = { columnType: 'operations', dataField: null, label: null, minLength: 30, allowHide: false, allowSort: false }
      if (this.selectionMode === 'multi') {
        this.head.push(selection)
      }
      this.head.push(opt)
      for (let t = 0; t < visibleRows.length; t++) {
        let row = visibleRows[t]
        this.head.push(row)
      }
      if (!this.firstHead) {
        this.firstHead = this.head.map(h => ({...h}))
      }
      //this.searchOnTable()
    },
    getFormattedDate (defaultValue) {
      return defaultValue && defaultValue.length === 2 && defaultValue[0] && defaultValue[0].slice && defaultValue[1] && defaultValue[1].slice ? `${defaultValue[0].slice(0, 10)} - ${defaultValue[1].slice(0, 10)}` : ''
    },
    setSearchQ (tableField, model) {
      searchQ[tableField] = model
    },
    getAndConditionModel (andConditionModels) {
      let model = {}
      if (andConditionModels) {
        model = JSON.parse(`{${decodeURI(andConditionModels)}}`)
      }
      return model
    },
    validateAction (actionName) {
      switch (this.$route.name) {
        case 'PurchaseOrder':
          let stateId = this.modalItem.StateId

          if (stateId === 3) {
            switch (actionName) {
              case 'Approve':
                this.$toasted.show(this.$t('insert.order.approveError'), {
                  type: 'error',
                  keepOnHover: true,
                  duration: '3000'
                })
                return false
              case 'Canceled':
                this.$toasted.show(this.$t('insert.order.cancelledError'), {
                  type: 'error',
                  keepOnHover: true,
                  duration: '3000'
                })
                return false
              case 'OrderConvert':
                this.$toasted.show(this.$t('insert.order.orderConvertError'), {
                  type: 'error',
                  keepOnHover: true,
                  duration: '3000'
                })
                return false
            }
          }
      }
      return true
    },
    filterRowActions (actions, item) {
      if (this.actionCondition && this.actionCondition.condition(item)) {
        return actions.filter(a => this.actionCondition.actions.includes(a.Action))
      }
      return actions
    },
    visibleWorkFlow (item) {
      if (this.actionCondition && this.actionCondition.condition(item)) {
        return false
      }
      return true
    },
    isItemInDispatchList (itemId) { // Verilen itemId'nin otherDispatchMultiPrintList içinde olup olmadığını kontrol eder
      if ((this.otherDispatchMultiPrintList.length < 2)) {
        return false
      } else {
        for (let i = 1; i < this.otherDispatchMultiPrintList.length; i++) {
          if ((itemId === this.otherDispatchMultiPrintList[i].id)) {
            return true
          }
        }
        return false
      }
    },
    updateRowAfterMultiPrint (itemId, option) { // Çoklu Yazdırılmak istenilen kayıtların tablodaki rengini ve hover mesajını değiştirir
      for (let i = 1; i < this.otherDispatchMultiPrintList.length; i++) {
        if ((itemId === this.otherDispatchMultiPrintList[i].id) && (this.otherDispatchMultiPrintList[i].isCompleted === false)) {
          return option === 'color' ? 'danger' : this.otherDispatchMultiPrintList[i].message
        } else if ((itemId === this.otherDispatchMultiPrintList[i].id) && (this.otherDispatchMultiPrintList[i].isCompleted === true)) {
          return option === 'color' ? 'success' : 'E-İrsaliye süreci başlatılmıştır'
        }
      }
    }
  },
  watch: {
    $route (to, from) {
      if (to.query.count) {
        this.perPage = parseInt(to.query.count)
      } else {
        this.perPage = 20
      }
      if (to.query.page) {
        this.currentPage = parseInt(to.query.page)
      } else {
        this.currentPage = 1
      }
      if (to.query.sort) {
        this.sort = to.query.sort
        this.sortField = to.query.field
      }

      let validCount = 0
      this.requiredFields.forEach(r => {
        if (searchQ[r.field] || this.AndConditionalModel[r.field] || this.AndConditionalModel[`${r.field}Ids`]) {
          validCount++
        }
      })
      if (validCount < this.requiredFields.length) {
        return
      }
      if (this.$route.query.code && this.$route.query.code.length > 0) {
        let sortOpt = {}
        if (this.$route.query.sort) {
          this.sort = this.$route.query.sort
          this.sortField = this.$route.query.field
          sortOpt = {
            table: this.sortField,
            sort: this.sort
          }
        } else {
          sortOpt = null
        }
        this.getData(this.$route.name, this.currentPage, this.perPage, sortOpt, true)
      } else {
        this.searchOnTable()
      }
    },
    tableRows: function (e) {
      this.setRows()
    },
    nextgrid: function (e) {
      // tablo datası yeniden yüklendiğinde bu bölüm çalıştırılacak.
      if (e === true) {
        this.currentPage = this.tableData.CurrentPage
        this.items = this.tableData.BaseModels
        this.totalRowCount = this.tableData.TotalRowCount
        this.totalPageCount = this.tableData.TotalPageCount
        this.perPage = this.tableData.PageRecordCount
      }
    },
    filtersCleared: function (e) {
      if (e === true) {
        this.AndConditionalModel = {}
        searchQ = {}
        if (this.firstSearchItem) {
          this.AndConditionalModel = {...this.firstSearchItem.andConditionalModel}
          searchQ = { ...this.firstSearchItem.searchQ }
        }

        let autoCompleteDropdowns = this.$refs.AutoCompleteDropdown
        if (autoCompleteDropdowns && autoCompleteDropdowns.length > 0) {
          autoCompleteDropdowns.forEach(a => {
            a.value = ''
          })
        }
        this.head = this.firstHead.map(h => ({...h}))
        if (this.lastGridItem && this.lastGridItem.BaseModels) {
          this.currentPage = this.lastGridItem.CurrentPage
          this.items = this.lastGridItem.BaseModels
          this.totalRowCount = this.lastGridItem.TotalRowCount
          this.totalPageCount = this.lastGridItem.TotalPageCount
          this.perPage = this.lastGridItem.PageRecordCount
        } else {
          this.searchOnTable()
        }
        let query = Object.assign({}, this.$route.query)
        delete query.code
        if (this.$router.history && this.$router.history.current && JSON.stringify(this.$router.history.current.query) !== JSON.stringify(query)) {
          this.$router.replace({ query })
        }
        this.$store.commit('changeFiltersCleared', false)
      }
    },
    reloadGrid: function (e) {
      if (e === true) {
        this.searchOnTable()
        this.$store.commit('setReloadGrid', false)
      }
    },
    isGridFieldsReady: function (e) {
      this.setRowsAfter()
    },
    isLookupReady: function (e) {
      this.setRowsAfter()
    }
  }
}

</script>
<style lang="sass">
  .asc__nextgrid
    position: relative
    height: 85vh
    .asc__nextgrid-table
      height: 81vh
      max-height: inherit
      margin-bottom: 0px
      .multi-hover-class
        &:hover
          cursor: pointer !important
      .autocomplete-search
        .autocomplete
          text-align: center
        .autocomplete-input
          font-size: 12px
          outline: none
          padding-left: 10px
          background-image: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjNjY2IiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCI+PGNpcmNsZSBjeD0iMTEiIGN5PSIxMSIgcj0iOCIvPjxwYXRoIGQ9Ik0yMSAyMWwtNC00Ii8+PC9zdmc+)
          background-repeat: no-repeat
          background-position: center right 10px
          background-size: 16px
          background-color: white
          border-radius: 5px
          border: solid 1px lightgray
        input::-webkit-input-placeholder
          color: #656665
          text-transform: initial !important
        .autocomplete-result-list
          line-height: 1.15
          padding: 0
          margin-top: 5px
        .autocomplete-result
          cursor: pointer
          padding: 10px
          text-align: left
          border-bottom: 0.5px solid rgba(0,0,0,.16)
          background: none
          font-size: 12px
    .router-link-exact-active
      background: #007bff
      color: #fff
    .dropdown-menu
      font-size: 12px
    .asc__nextgrid-dropdown-btn-p0
      .dropdown-toggle
        padding: 0px 5px
    .asc__nextgrid-paginationlinks
      & a
        color: #000
      .router-link-exact-active
        background-color: #fc9e01
        border-color: #f99a03
    .asc__nextgrid-table-header-LabelValue
      min-width: 100px
    .asc__nextgrid-table-header-Boolean
      width: 120px
    .asc__nextgrid-table-header-Date
      width: 170px
    .asc__nextgrid-table-header-DateTime
      width: 170px
    .asc__nextgrid-table-header-String
      min-width: 100px
    .asc__nextgrid-table-header-Id
      width: 120px
    .asc__nextgrid-table-header
      background: #f2f2f2
      top: -3px !important
      .asc__nextgrid-table-header-title
        display: inline-block
        cursor: move
      .asc__nextgrid-table-header-sort
        cursor: pointer
        z-index: 2
        overflow: none
        & button
          color: #8a8a8a
          background: none
          border: none
          margin-top: -7px
        .asc__nextgrid-table-header-sort-active
          color: #000
          border: none
      .asc__nextgrid-table-header-filter
        position: relative
        input
          height: 28px
    .asc__nextgrid-table-footer
      padding: 10px 0 0 0
      height: 50px
      margin: auto
      width: 100%
      background: #f2f2f2
      .dropdown-toggle
        background: #ffffff
        color: #000
        border-color: #dee2e6
    .row-selected
      background-color: rgb(222, 226, 230)
      border: solid 2px
      border-color: darkgray
      &:hover
        cursor: pointer
    .selected-color
      color: #28a745
    .unselected-color
      color: lightgray
    .grid-wrap-text
      white-space: nowrap
      overflow: hidden
      text-overflow: ellipsis
      max-width: 200px
      min-width: 50px
    .mx-datepicker-range
      width: 160px
</style>



// WEBPACK FOOTER //
// src/components/NextGrid.vue